import type { Database } from "@/integrations/supabase/types";

export type AppRole = Database["public"]["Enums"]["app_role"];

export const ROLES = {
  OWNER: "owner",
  ADMIN: "admin",
  MANAGER: "manager",
  CASHIER: "cashier",
  KITCHEN: "kitchen",
  STAFF: "staff",
} as const satisfies Record<string, AppRole>;

export const ROLE_GROUPS = {
  ADMIN: [ROLES.OWNER, ROLES.ADMIN] as const satisfies readonly AppRole[],
  MANAGEMENT: [
    ROLES.OWNER,
    ROLES.ADMIN,
    ROLES.MANAGER,
  ] as const satisfies readonly AppRole[],
  POS: [
    ROLES.OWNER,
    ROLES.ADMIN,
    ROLES.MANAGER,
    ROLES.CASHIER,
  ] as const satisfies readonly AppRole[],
  KITCHEN: [
    ROLES.OWNER,
    ROLES.ADMIN,
    ROLES.MANAGER,
    ROLES.KITCHEN,
  ] as const satisfies readonly AppRole[],
  ALL: [
    ROLES.OWNER,
    ROLES.ADMIN,
    ROLES.MANAGER,
    ROLES.CASHIER,
    ROLES.KITCHEN,
    ROLES.STAFF,
  ] as const satisfies readonly AppRole[],
} as const;

export const APP_ROLES: readonly AppRole[] = ROLE_GROUPS.ALL;
export const DEFAULT_SIGNUP_ROLE: AppRole = ROLES.STAFF;

export function isAppRole(value: string): value is AppRole {
  return (APP_ROLES as readonly string[]).includes(value);
}

export function isAdmin(role: AppRole | null | undefined): boolean {
  return role === ROLES.OWNER || role === ROLES.ADMIN;
}

export function isManagement(role: AppRole | null | undefined): boolean {
  return role === ROLES.OWNER || role === ROLES.ADMIN || role === ROLES.MANAGER;
}

export function isSuperAdmin(profile: unknown): boolean {
  if (!profile || typeof profile !== "object") return false;
  if (!("is_superadmin" in profile)) return false;
  const v = Reflect.get(profile, "is_superadmin");
  return v === true;
}
